import type { ErrorEvent, EventHint } from '@sentry/angular';

export interface HttpLikeError {
  status: number;
  statusText?: string;
  url?: string;
  message?: string;
}

const SUPPRESSED_HTTP_400_URL_PATTERNS = [
  '/CommentByReferenceIds/',
  '/CountersId/RegistryUniqueIds',
  '/user/infos/cached',
];

const ICON_SET_LOAD_FAILURE_PATTERN = /Loading icon set URL:.*failed:/i;

export function isHttpLikeError(error: unknown): error is HttpLikeError {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const candidate = error as HttpLikeError;
  const hasStatus = typeof candidate.status === 'number';
  const hasUrl = typeof candidate.url === 'string';
  const hasHttpFailureMessage = typeof candidate.message === 'string' && candidate.message.includes('Http failure response');

  return hasStatus && (hasUrl || hasHttpFailureMessage);
}

export function getHttpErrorMessage(error: HttpLikeError): string {
  if (error.message) {
    return error.message;
  }

  const statusText = error.statusText ? ` ${error.statusText}` : '';
  const url = error.url ? ` for ${error.url}` : '';
  return `HTTP ${error.status}${statusText}${url}`.trim();
}

export function shouldSuppressErrorFromSentry(error: unknown): boolean {
  if (error instanceof Error && ICON_SET_LOAD_FAILURE_PATTERN.test(error.message)) {
    return true;
  }

  if (isHttpLikeError(error)) {
    return shouldSuppressHttpErrorFromSentry(error);
  }

  return false;
}

export function shouldSuppressHttpErrorFromSentry(error: HttpLikeError): boolean {
  if (error.status === 0) {
    return true;
  }

  if (error.status !== 400) {
    return false;
  }

  const target = `${error.url ?? ''} ${error.message ?? ''}`;

  if (target.includes('assets/icons/')) {
    return true;
  }

  return SUPPRESSED_HTTP_400_URL_PATTERNS.some((pattern) => target.includes(pattern));
}

export function applyHttpErrorToSentryEvent(event: ErrorEvent, error: HttpLikeError): ErrorEvent {
  const message = getHttpErrorMessage(error);
  const exception = event.exception ?? { values: [] };
  const values = exception.values ?? [];

  if (values.length === 0) {
    values.push({
      type: 'HttpErrorResponse',
      value: message,
    });
  } else {
    values[0] = {
      ...values[0],
      type: values[0].type === 'Error' || !values[0].type ? 'HttpErrorResponse' : values[0].type,
      value: message,
    };
  }

  event.exception = {
    ...exception,
    values,
  };

  if (!event.message) {
    event.message = message;
  }

  return event;
}

export function processSentryEvent(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
  const originalException = hint.originalException;

  if (shouldSuppressErrorFromSentry(originalException)) {
    return null;
  }

  if (!isHttpLikeError(originalException)) {
    return event;
  }

  return applyHttpErrorToSentryEvent(event, originalException);
}
